import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Extension as ExtensionIcon
} from '@mui/icons-material';

/**
 * IntegrationCard component displays an integration in a card format
 * 
 * @param {Object} props
 * @param {Object} props.integration - The integration object
 * @param {Function} props.onEdit - Function to call when edit button is clicked
 * @param {Function} props.onDelete - Function to call when delete button is clicked
 */
const IntegrationCard = ({ integration, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleEdit = () => {
    handleMenuClose();
    onEdit(integration);
  };
  
  const handleDelete = () => {
    handleMenuClose();
    onDelete(integration);
  };
  
  // Get tool icon or use default
  const getToolIcon = () => {
    if (integration.Tool?.icon) {
      return integration.Tool.icon;
    }
    return <ExtensionIcon />;
  };
  
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ mr: 1, bgcolor: 'rgba(0, 0, 0, 0.04)', height: '72px', width: '72px' }}>
              {typeof getToolIcon() === 'string' ? (
                <img src={getToolIcon()} alt={integration.Tool?.name || 'Tool'} width="56" height="56" />
              ) : (
                getToolIcon()
              )}
            </Avatar>
            <Typography variant="h6" component="div" noWrap>
              {integration.name}
            </Typography>
          </Box>
          <Box>
            <Chip 
              label={integration.isUsed ? 'Used' : 'Not Used'} 
              size="small"
              color={integration.isUsed ? 'success' : 'default'}
            />
          </Box>
        </Box>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Tool: {integration.Tool?.name || 'Unknown'}
        </Typography>
        
        {integration.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {integration.description}
          </Typography>
        )}
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
        <Tooltip title="Edit">
          <Button 
            size="small" 
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Edit
          </Button>
        </Tooltip>
        <Tooltip title="Delete">
          <Button 
            size="small" 
            color="error" 
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

export default IntegrationCard;